import styled, { css } from 'styled-components';
import { shimmer, slideUp } from '~styles/animations';
import { COLORS, SURFACES } from '~styles/tokens';
import { CATEGORY_COLORS } from './constants';


export const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9998;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

export const SlidePanel = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? '0' : '-500px'};
  width: 480px;
  max-width: 100%;
  height: 100vh;
  background: ${SURFACES.card};
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  transition: right 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 520px) {
    width: 100%;
    right: ${props => props.isOpen ? '0' : '-100%'};
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  h2 {
    font-size: 1.375rem;
    font-weight: 700;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    svg {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
`;

export const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  cursor: pointer;
  padding: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: all 0.2s;
  color: white;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: rotate(90deg);
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

export const TabsContainer = styled.div`
  display: flex;
  padding: 0 1.75rem;
  background: ${SURFACES.inset};
  border-bottom: 1px solid ${COLORS.borderDefault};
`;

export const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 1rem 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.active ? '#667eea' : '#64748b'};
  position: relative;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    color: ${props => props.active ? '#667eea' : '#334155'};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${props => props.active ? '80%' : '0'};
    height: 3px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 3px 3px 0 0;
    transition: width 0.3s ease;
  }
`;

export const TabBadge = styled.span<{ variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.375rem;
  height: 1.375rem;
  padding: 0 0.375rem;
  background: ${props => props.variant === 'secondary'
    ? COLORS.borderDefault
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${props => props.variant === 'secondary' ? '#64748b' : 'white'};
  font-size: 0.6875rem;
  font-weight: 700;
  border-radius: 0.75rem;
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.75rem;
  background: ${SURFACES.card};
  border-bottom: 1px solid #f1f5f9;
  flex-wrap: wrap;
`;

export const CategoryPill = styled.button<{ active: boolean; color?: string }>`
  padding: 0.5rem 0.875rem;
  background: ${props => props.active
    ? props.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : '#f1f5f9'};
  border: none;
  border-radius: 2rem;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.active ? 'white' : '#64748b'};
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  &:hover {
    background: ${props => props.active
      ? props.color || 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
      : COLORS.borderDefault};
    transform: translateY(-1px);
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

export const NotificationListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.75rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

export const DateGroup = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const DateHeader = styled.h3`
  font-size: 0.6875rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.075em;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
`;

export const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const getCategoryColor = (category?: string): string => {
  return CATEGORY_COLORS[category || ''] || '#64748b';
};

export const NotificationCard = styled.div<{ completed?: boolean; category?: string }>`
  background: ${props => props.completed ? SURFACES.inset : SURFACES.card};
  border: 1px solid ${props => props.completed ? COLORS.neutral200 : COLORS.borderDefault};
  border-radius: 0.75rem;
  padding: 1rem;
  position: relative;
  overflow: hidden;
  transition: all 0.25s ease;
  animation: ${slideUp} 0.3s ease;
  opacity: ${props => props.completed ? 0.65 : 1};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.completed
      ? '#cbd5e1'
      : getCategoryColor(props.category)};
    border-radius: 4px 0 0 4px;
  }

  &:hover {
    border-color: ${props => !props.completed && getCategoryColor(props.category)};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateX(4px);
  }
`;

export const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

export const NotificationIconWrapper = styled.div<{ category?: string }>`
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => `${getCategoryColor(props.category)}15`};
  border-radius: 0.625rem;
  color: ${props => getCategoryColor(props.category)};

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const NotificationBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const NotificationTitle = styled.h4<{ completed?: boolean }>`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${props => props.completed ? COLORS.textMuted : COLORS.textPrimary};
  margin: 0 0 0.25rem 0;
  text-decoration: ${props => props.completed ? 'line-through' : 'none'};
  line-height: 1.4;
`;

export const NotificationDescription = styled.p`
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0 0 0.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const ClientInfo = styled.span`
  font-size: 0.75rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

export const TimeInfo = styled.span`
  font-size: 0.6875rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

export const CategoryBadge = styled.span<{ category?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: ${props => `${getCategoryColor(props.category)}15`};
  color: ${props => getCategoryColor(props.category)};
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

export const NotificationActions = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-left: auto;
  opacity: 0.5;
  transition: opacity 0.2s;

  ${NotificationCard}:hover & {
    opacity: 1;
  }
`;

export const ActionButton = styled.button<{ variant?: 'view' | 'check' | 'delete' }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: all 0.2s;
  color: ${COLORS.textMuted};

  ${props => props.variant === 'view' && css`
    &:hover {
      background: ${COLORS.infoLight};
      color: ${COLORS.info};
    }
  `}

  ${props => props.variant === 'check' && css`
    &:hover {
      background: ${COLORS.successLight};
      color: #10b981;
    }
  `}

  ${props => props.variant === 'delete' && css`
    &:hover {
      background: ${COLORS.dangerLight};
      color: ${COLORS.danger};
    }
  `}

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const EmptyIcon = styled.div`
  width: 5rem;
  height: 5rem;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;

  svg {
    width: 2.5rem;
    height: 2.5rem;
    color: #94a3b8;
  }
`;

export const EmptyTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #334155;
  margin: 0 0 0.5rem 0;
`;

export const EmptyDescription = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  max-width: 280px;
  line-height: 1.5;
`;

export const LoadingCard = styled.div`
  background: ${SURFACES.card};
  border: 1px solid ${COLORS.borderDefault};
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
`;

export const LoadingLine = styled.div<{ width?: string }>`
  height: 0.75rem;
  width: ${props => props.width || '100%'};
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: ${COLORS.dangerLight};
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  color: ${COLORS.dangerHover};
  font-size: 0.875rem;
  margin: 1rem 1.75rem;

  svg {
    flex-shrink: 0;
    width: 1.25rem;
    height: 1.25rem;
  }
`;

